import { useCallback, useEffect, useState } from 'react';
import '../../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { useDeleteFlow } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { DateRangeSelect } from '../../modules/DateRangeSelect';
import { fetchOpenQuotes } from './api';
import { QUOTE_COLUMNS } from './columns';
import { deleteQuotes, downloadQuoteReport, reportErrorMessage } from './detailApi';
import QuoteDetailPage from './QuoteDetailPage';
import { useQuoteSearch } from './useQuoteSearch';
import {
  QUOTE_DEFAULT_FILTER, QUOTE_RANGES, rangeCode, rangeDays,
  type QuoteDateFilter, type QuoteListRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes ▸ Open Quotes (nav `acct-quotes-open`).
// Port of legacy ALPACA `CRMQuotes_ListView_pendingsubmission`
// (BTLIMS.Web/Model.xafml :14104), under the nav group `Accounting\Quotes`.
//
// The quotes still in play: not cancelled, not lapsed, and either Pending
// Submission or Pending Validation — narrowed by the Date Filter chip. Those
// criteria live in `dbo.AI_Quotes_OpenList_SP`, not here, so the screen cannot
// disagree with an export or with any later report (§18.5, one source of truth).
//
// TOOLBAR — legacy: New · Edit · Delete · Preview · Date Filter · Refresh ·
// Export to · Search. The standard actions lead the form-specific ones here, so
// the shared <AppToolbar crudFirst> opt-in is used (owner decision 2026-10-13);
// this is the second form to pass it, after Item Initialization. With
// `crudFirst` the Date Filter sits just before Preview rather than just after
// it — the one difference from the legacy order, accepted rather than adding a
// fourth ordering option to the shared toolbar.
//
// New and Edit open the full-page Open Quote detail view, which REPLACES this
// list (standing rule 12). A row also opens on double-click, as in legacy.
//
// AUDIT. FormName "Open Quotes" — the name legacy already writes, because it
// audits by the nav caption clicked (AuditlogViewController :2065).
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Open Quotes';

type Screen = { kind: 'list' } | { kind: 'detail'; id: string | null; key: number };

export default function OpenQuotesPage() {
  const notify    = useNotify();
  const runDelete = useDeleteFlow();
  const [rows, setRows]       = useState<QuoteListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<QuoteDateFilter>(QUOTE_DEFAULT_FILTER);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });

  // The toolbar Search box filters the rows already fetched — it does NOT
  // widen the Date Filter, so a quote outside the chosen window stays out
  // until the chip is widened. Same as legacy.
  const shown = useQuoteSearch(rows, search);

  const load = useCallback(async (filter?: QuoteDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchOpenQuotes(filter);
      setRows(res.rows);
      // The chip the SERVER applied, not the one we asked for.
      setDateFilter(res.dateFilter);
      // A reload can drop rows; a surviving selection would point at nothing.
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load open quotes.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  function open(id: string | null) {
    // A fresh key every time, so "New" after "New" really is a blank form.
    setScreen({ kind: 'detail', id, key: Date.now() });
  }

  async function deleteSelected() {
    const ids = [...selected].map(String);
    if (ids.length === 0) return;
    const numbers = rows.filter((r) => ids.includes(r.oid)).map((r) => r.quoteID).filter(Boolean);
    await runDelete({
      formName: FORM_NAME,
      message: ids.length === 1 ? `Delete quote ${numbers[0] ?? ''}?` : `Delete ${ids.length} quotes?`,
      // All-or-nothing on the server: if any selected quote is used in sample
      // registration, none is deleted and the refusal carries legacy's wording.
      delete: async () => { await deleteQuotes(ids); },
      onDeleted: () => { void load(dateFilter); },
    });
  }

  /** Preview — legacy `btnqrpreview`, which needs exactly ONE selected quote
   *  (`SelectionDependencyType.RequireSingleObject`). */
  async function preview() {
    if (selected.size !== 1) {
      // Legacy wording, verbatim (LDMMessages "selectchkbox").
      notify.warning('Please select an item to proceed further.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(String([...selected][0]));
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      // A quote with no pricing rows is refused in legacy's words
      // ("Add pricecode and continue.").
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  // ── Detail view (New or Edit) — replaces the list, never a popup (rule 12)
  if (screen.kind === 'detail') {
    return (
      <QuoteDetailPage
        key={screen.key}
        id={screen.id}
        siblingIds={shown.map((r) => r.oid)}
        onNavigate={(id) => open(id)}
        onClose={() => setScreen({ kind: 'list' })}
        onNew={() => open(null)}
        onCreated={(id) => open(id)}
        onChanged={() => void load(dateFilter)}
      />
    );
  }

  const one = selected.size === 1 ? String([...selected][0]) : null;

  return (
    <div className="bl-page">
      <FormTitle pageId="acct-quotes-open" loadMs={loadMs} loading={loading}
                 auditFormName={FORM_NAME} />
      <AppToolbar
        crudFirst
        onNew={() => open(null)}
        onEdit={() => { if (one) open(one); }}
        editDisabled={!one}
        onDelete={() => void deleteSelected()}
        deleteDisabled={selected.size === 0}
        left={
          <DateRangeSelect
            variant="chips" label="Date Filter" ranges={QUOTE_RANGES}
            value={rangeDays(dateFilter)}
            onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
          />
        }
        onPreview={() => void preview()}
        previewDisabled={selected.size !== 1}
        onRefresh={() => load(dateFilter)}
        refreshing={loading}
        exportData={{ filename: 'open-quotes', ...gridExportMatrix(shown, QUOTE_COLUMNS) }}
      />
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<QuoteListRow>
        rows={shown}
        columns={QUOTE_COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowDoubleClick={(r) => open(r.oid)}
        emptyText={search ? 'No results match your search.' : 'No open quotes for this date range.'}
      />
    </div>
  );
}
